import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

/* ── global search (Ctrl+K) ── */
export type SearchSectionKey = "customers" | "users" | "tasks" | "forms" | "categories" | "funds" | "suppliers" | "meetings";
export type SearchSectionDto = {
  key: SearchSectionKey;
  title: string;
  items: Array<{ id: string; label: string; sub: string | null; path: string }>;
};

/* ── Today ── */
export type TodayMoneyBlock = { amountAgorot: number; count: number; items: Array<{ id: string; name: string; amountAgorot: number }> };
export type TodayAttentionDto = {
  key: string;
  title: string;
  icon: string;
  tone: "danger" | "warning" | "info" | "primary";
  count: number;
  amountAgorot?: number;
  path: string;
  label: string;
};
export type TodayTaskDto = {
  id: string;
  title: string;
  dueAt: string | null;
  overdue: boolean;
  priority: string;
  status: string;
  assigneeName: string | null;
  customerName: string | null;
};
export type TodayDto = {
  date: string;
  generatedAt: string;
  money: {
    payments: TodayMoneyBlock | null;
    incomes: TodayMoneyBlock | null;
    expenses: TodayMoneyBlock | null;
    paymentsMonthAgorot: number | null;
  };
  attention: TodayAttentionDto[];
  tasks: {
    mine: TodayTaskDto[];
    general: TodayTaskDto[];
    counts: { upcomingMine: number; unassigned: number; overdueAll: number };
    canSeeTeam: boolean;
  } | null;
  meetings: Array<{ id: string; title: string; scheduledAt: string | null; status: string }> | null;
  customers: { newToday: number; newWeek: number } | null;
};

/* ── automations ── */
export type AutomationTriggerDto =
  | {
      type: "schedule";
      every: "day" | "week" | "month" | "once";
      time: string;
      weekday?: number;
      monthDay?: number;
      date?: string;
      audience: string;
      audienceDays?: number;
      oncePerRecord: boolean;
    }
  | { type: "event"; event: string; formId?: string };

export type AutomationConditionDto = { field: string; op: string; value?: string | number };

export type AutomationRecipientsDto = {
  mode: "record" | "assignee" | "actor" | "users" | "emails";
  userIds?: string[];
  emails?: string[];
};

export type AutomationActionDto =
  | { type: "send_email"; to: AutomationRecipientsDto; subject: string; body: string }
  | {
      type: "create_task";
      title: string;
      description?: string;
      assignTo: { mode: "user" | "unassigned" | "actor" | "assignee"; userId?: string };
      dueInDays: number;
      priority: "low" | "normal" | "high" | "urgent";
    }
  | { type: "send_report"; report: string; to: AutomationRecipientsDto; days?: number };

export type AutomationDto = {
  id: string;
  name: string;
  description: string | null;
  screenKey: string;
  kind: "schedule" | "event";
  status: "active" | "paused" | "error";
  templateKey: string | null;
  trigger: AutomationTriggerDto;
  conditions: AutomationConditionDto[];
  actions: AutomationActionDto[];
  summary: string;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastError: string | null;
  runCount: number;
  /** manual | template | ai (ADR-0035) */
  createdVia?: string;
  aiPrompt?: string | null;
  createdAt: string;
  updatedAt: string;
};

/** The automation designer (ADR-0035) — POST /automations/ai/design */
export type AutomationDesignMessage = { role: "user" | "assistant"; text: string };
export type AutomationDesignDto = {
  status: "ready" | "need_info" | "exists" | "unsupported";
  reply: string;
  questions: string[];
  insights: string[];
  matches: Array<{ type: "automation" | "template"; id: string; name: string; reason: string }>;
  automation: AutomationInput | null;
  summary: string | null;
  preview: { considered: number; matched: number; sample: string[] } | null;
  analysis: Array<{ label: string; summary: string }>;
};

export type AutomationInput = {
  name: string;
  description?: string | null;
  screenKey: string;
  templateKey?: string | null;
  status: "active" | "paused";
  trigger: AutomationTriggerDto;
  conditions: AutomationConditionDto[];
  actions: AutomationActionDto[];
};

export type AutomationRunDto = {
  id: string;
  trigger: string;
  status: string;
  summary: {
    considered?: number;
    matched?: number;
    skippedDone?: number;
    emailsSent?: number;
    emailsFailed?: number;
    tasksCreated?: number;
    reportsSent?: number;
    skippedNoRecipient?: number;
    errors?: string[];
    dryRun?: boolean;
  };
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
};

export type CatalogField = {
  key: string;
  label: string;
  type: "text" | "number" | "money" | "enum" | "date";
  options?: Array<{ value: string; label: string }>;
};

export type AutomationTemplateDto = {
  key: string;
  screen: string;
  icon: string;
  name: string;
  description: string;
  trigger: AutomationTriggerDto;
  conditions: AutomationConditionDto[];
  actions: AutomationActionDto[];
};

export type AutomationCatalogDto = {
  screens: Array<{ key: string; label: string; paths: string[]; icon: string }>;
  events: Array<{ key: string; label: string; screen: string; entity: string }>;
  audiences: Array<{ key: string; label: string; hint: string; screen: string; entity: string }>;
  reports: Array<{ key: string; label: string; hint: string }>;
  actions: Array<{ key: string; label: string; icon: string }>;
  fields: Record<string, CatalogField[]>;
  commonTags: CatalogField[];
  conditionOps: Record<string, string>;
  templates: AutomationTemplateDto[];
  staff: Array<{ id: string; name: string; hasEmail: boolean }>;
};

export type AutomationRunResult = {
  runId: string;
  status: string;
  summary: AutomationRunDto["summary"];
  errorMessage: string | null;
};

export const nextApi = {
  search(q: string): Promise<SearchSectionDto[]> {
    return wrap(async () => (await httpClient.get<{ sections: SearchSectionDto[] }>("/search", { params: { q } })).data.sections);
  },

  today(): Promise<TodayDto> {
    return wrap(async () => (await httpClient.get<{ item: TodayDto }>("/today")).data.item);
  },

  automations: {
    catalog(): Promise<AutomationCatalogDto> {
      return wrap(async () => (await httpClient.get<{ item: AutomationCatalogDto }>("/automations/catalog")).data.item);
    },
    counts(): Promise<{ counts: Record<string, number>; errors: number }> {
      return wrap(async () => (await httpClient.get<{ item: { counts: Record<string, number>; errors: number } }>("/automations/counts")).data.item);
    },
    list(): Promise<AutomationDto[]> {
      return wrap(async () => (await httpClient.get<{ items: AutomationDto[] }>("/automations")).data.items);
    },
    get(id: string): Promise<AutomationDto> {
      return wrap(async () => (await httpClient.get<{ item: AutomationDto }>(`/automations/${id}`)).data.item);
    },
    runs(id: string): Promise<AutomationRunDto[]> {
      return wrap(async () => (await httpClient.get<{ items: AutomationRunDto[] }>(`/automations/${id}/runs`)).data.items);
    },
    create(input: AutomationInput & { createdVia?: "manual" | "template" | "ai"; aiPrompt?: string | null }): Promise<AutomationDto> {
      return wrap(async () => (await httpClient.post<{ item: AutomationDto }>("/automations", input)).data.item);
    },
    fromTemplate(templateKey: string): Promise<AutomationDto> {
      return wrap(async () => (await httpClient.post<{ item: AutomationDto }>("/automations/from-template", { templateKey })).data.item);
    },
    update(id: string, input: AutomationInput): Promise<AutomationDto> {
      return wrap(async () => (await httpClient.put<{ item: AutomationDto }>(`/automations/${id}`, input)).data.item);
    },
    setStatus(id: string, status: "active" | "paused"): Promise<AutomationDto> {
      return wrap(async () => (await httpClient.post<{ item: AutomationDto }>(`/automations/${id}/status`, { status })).data.item);
    },
    run(id: string, dryRun = false): Promise<AutomationRunResult> {
      return wrap(async () => (await httpClient.post<{ item: AutomationRunResult }>(`/automations/${id}/run`, { dryRun })).data.item);
    },
    remove(id: string): Promise<void> {
      return wrap(async () => {
        await httpClient.delete(`/automations/${id}`);
      });
    },
    /** the automation designer — a few model calls, can take a while */
    design(input: { screenKey?: string | null; draft?: unknown; messages: AutomationDesignMessage[] }): Promise<AutomationDesignDto> {
      return wrap(async () => (await httpClient.post<{ item: AutomationDesignDto }>("/automations/ai/design", input, { timeout: 180_000 })).data.item);
    },
  },
};
